import React, { useEffect, useRef, useState } from 'react';
import { adminAPI, TransactionCallbackState } from '../../lib/api';
import { Button } from '../ui/button';

const labels: Record<TransactionCallbackState['event']['status'], string> = {
    unknown: 'Нет сохранённых данных о доставке', pending: 'В очереди',
    sending: 'Отправляется', retry: 'Ожидается повторная попытка',
    delivered: 'Получен HTTP 200', superseded: 'Отменено: сделка изменилась',
};
const date = (value: string) => new Date(/(?:Z|[+-]\d{2}:\d{2})$/.test(value) ? value : `${value}Z`).toLocaleString('ru-RU');

export type TransactionCallbackAPI = Pick<typeof adminAPI, 'getTransactionCallback' | 'resendTransactionCallback'>;
export const TransactionCallbackPanel: React.FC<{
    transactionId: string; apiClient?: TransactionCallbackAPI; storageScope?: string;
}> = ({ transactionId, apiClient = adminAPI, storageScope = 'admin' }) => {
    const [state, setState] = useState<TransactionCallbackState | null>(null);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const inFlight = useRef(false);
    const loaded = useRef(false);
    const generation = useRef(0);
    const key = `${storageScope}-callback-request:${transactionId}`;
    const requestId = useRef<string | null>(localStorage.getItem(key));
    const client = useRef(apiClient);
    client.current = apiClient;
    const mounted = useRef(true);
    const active = !state || ['pending', 'retry', 'sending'].includes(state.event.status);

    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);
    useEffect(() => {
        let cancelled = false;
        let loading = false;
        const refresh = async () => {
            if (loading || inFlight.current) return;
            loading = true;
            const version = generation.current;
            try {
                const value = await client.current.getTransactionCallback(transactionId);
                if (!cancelled && version === generation.current) { loaded.current = true; setState(value); setError(''); }
            } catch {
                if (!cancelled && version === generation.current) setError('Не удалось обновить состояние колбэка. Повторите загрузку сделки.');
            } finally { loading = false; }
        };
        if (!loaded.current) void refresh();
        const timer = active ? window.setInterval(refresh, 3000) : undefined;
        return () => { cancelled = true; if (timer) window.clearInterval(timer); };
    }, [transactionId, active]);

    const resend = async () => {
        if (inFlight.current) return;
        inFlight.current = true;
        generation.current += 1;
        setBusy(true); setError('');
        try {
            const id = requestId.current || crypto.randomUUID();
            requestId.current = id;
            localStorage.setItem(key, id);
            const value = await client.current.resendTransactionCallback(transactionId, id);
            localStorage.removeItem(key); requestId.current = null;
            if (mounted.current) setState(value);
        } catch (e: any) {
            const definitive = [400, 403, 404, 409, 422].includes(e?.response?.status);
            if (definitive) { localStorage.removeItem(key); requestId.current = null; }
            if (mounted.current) {
                setError(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail
                    : 'Нет ответа сервера. Повторите запрос: повтор не создаст дубликат.');
                try {
                    const value = await client.current.getTransactionCallback(transactionId);
                    if (mounted.current) setState(value);
                } catch { /* Keep last known state. */ }
            }
        } finally {
            inFlight.current = false;
            if (mounted.current) setBusy(false);
        }
    };

    return <div className="rounded border p-3 space-y-2">
        <h3 className="font-semibold">Колбэк мерчанту</h3>
        <div role="status">{state ? labels[state.event.status] : 'Загрузка состояния…'}</div>
        {state?.event.status === 'delivered' && state.event.delivered_at && <p className="text-sm">Время ответа: {date(state.event.delivered_at)}</p>}
        {state && ['pending', 'retry'].includes(state.event.status) && state.event.next_attempt_at &&
            <p className="text-sm">Следующая попытка: {date(state.event.next_attempt_at)}</p>}
        {state?.event.status === 'retry' && state.event.error && <p className="text-sm">{state.event.error}</p>}
        {state?.blocked_reason && <p className="text-sm">{state.blocked_reason}</p>}
        {error && <p role="alert" className="text-sm text-red-700 dark:text-red-300">{error}</p>}
        <Button type="button" variant="outline" disabled={busy || !state?.can_resend} onClick={resend}>
            {busy ? 'Постановка в очередь…' : 'Отправить КБ повторно'}
        </Button>
        <p className="text-xs text-gray-500 dark:text-slate-400">Отправляет текущее состояние сделки без изменения суммы, статуса и балансов. HTTP 200 подтверждает ответ сервера мерчанта, но не обработку платежа в его системе.</p>
    </div>;
};
